import { formatDistanceToNow } from 'date-fns'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Ban, Trash2 } from 'lucide-react'
import { ApiKeyStatusBadge } from './api-key-status-badge'
import type { ApiKeyListItem } from '@/types/api-keys.types'

interface ApiKeyTableProps {
  apiKeys: ApiKeyListItem[]
  onRevoke: (id: string) => void
  onDelete: (id: string) => void
}

function getApiKeyStatus(isRevoked: boolean, isExpired: boolean): 'Active' | 'Expired' | 'Revoked' {
  if (isRevoked) return 'Revoked'
  if (isExpired) return 'Expired'
  return 'Active'
}

export function ApiKeyTable({ apiKeys, onRevoke, onDelete }: ApiKeyTableProps) {
  if (apiKeys.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">No API keys yet. Create your first API key to get started.</p>
      </div>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Last Used</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {apiKeys.map((apiKey) => {
          const status = getApiKeyStatus(apiKey.isRevoked, apiKey.isExpired)

          return (
            <TableRow key={apiKey.id}>
              <TableCell className="font-medium">{apiKey.name}</TableCell>
              <TableCell>
                <ApiKeyStatusBadge status={status} />
              </TableCell>
              <TableCell className="text-muted-foreground">
                {apiKey.lastUsedAt
                  ? formatDistanceToNow(new Date(apiKey.lastUsedAt), { addSuffix: true })
                  : 'Never'}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatDistanceToNow(new Date(apiKey.createdAt), { addSuffix: true })}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  {!apiKey.isRevoked && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onRevoke(apiKey.id)}
                      aria-label="Revoke API key"
                    >
                      <Ban className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(apiKey.id)}
                    aria-label="Delete API key"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
